import React, { Component } from "react";
import { Form, Button, Carousel } from "react-bootstrap";
import { withRouter } from "react-router-dom";
import { graphql } from "react-apollo";
import gql from "graphql-tag";
import * as Cookies from "es-cookie";
import ApolloClient from "apollo-boost";
import client from "../../apolloClient";
import { getFirstAccessibleRoute } from "../../moduleAccessGuard";
import { resolveUserScope } from "../../utils/dataScope";

import vignanLogo from "../../images/vignan_short_logo.png";
import "../../_role_login.scss";

// Student accounts live on a SEPARATE backend from the college/institution API.
// The studentLogin mutation must be sent here (not the app-wide college client),
// otherwise it returns no user and no session cookies get written.
const studentClient = new ApolloClient({
  uri: "https://rizee-student-backend-755214126997.us-south1.run.app/graphql",
});

// Each role card surfaces a different identifier field (username / mobile / email)
// + password. The chosen value is sent to the login1 mutation's `username` arg.
const ROLES = [
  {
    key: "student",
    title: "Student",
    sub: "Classes, exams & rank",
    icon: "fa-user-graduate",
    accent: "#2563eb",
    accent2: "#3b82f6",
    field: "username",
    label: "Username / Email",
    placeholder: "VJC2025007",
    type: "text",
    inputIcon: "fa-id-badge",
  },
  {
    key: "parent",
    title: "Parent",
    sub: "Follow your child's progress",
    icon: "fa-users",
    accent: "#059669",
    accent2: "#10b981",
    field: "username",
    label: "Username / Email",
    placeholder: "Username or email",
    type: "text",
    inputIcon: "fa-id-badge",
  },
  {
    key: "faculty",
    title: "Faculty",
    sub: "Run classes & attendance",
    icon: "fa-chalkboard-teacher",
    accent: "#d97706",
    accent2: "#f59e0b",
    field: "username",
    label: "Username / Email",
    placeholder: "Username or name@vignan.edu.in",
    type: "text",
    inputIcon: "fa-id-badge",
  },
  {
    key: "management",
    title: "Management",
    sub: "Full institute analytics",
    icon: "fa-building",
    accent: "#7c3aed",
    accent2: "#a855f7",
    field: "username",
    label: "Username / Email",
    placeholder: "Enter username",
    type: "text",
    inputIcon: "fa-id-badge",
  },
];

// Roles authenticated via the login1 (institution) mutation. Student and Parent
// are intentionally excluded until their login flows are implemented.
const LOGIN1_ROLES = ["faculty", "management"];

// Vignan runs multiple campuses across Hyderabad — the brand block auto-scrolls
// through them under the college name. Update this list with the real campuses.
const CAMPUSES = [
  "Nizampet",
  "Dilsukhnagar",
];

// Left-panel marketing copy swaps with the selected role card. Each role has a
// headline plus 3 carousel slides that auto-scroll to show more detail.
const ROLE_INTRO = {
  student: {
    headline: "Learn smarter.",
    accentWord: "Track better.",
    slides: [
      {
        title: "Study your way",
        points: [
          { icon: "fa-chalkboard-teacher", text: "Attend live & recorded classes" },
          { icon: "fa-book", text: "Access study material anytime" },
          { icon: "fa-sticky-note", text: "Chapter-wise notes & short notes" },
          { icon: "fa-play-circle", text: "Concept videos for every topic" },
          { icon: "fa-clock", text: "Learn at your own pace" },
        ],
      },
      {
        title: "Practice & prove it",
        points: [
          { icon: "fa-file-alt", text: "Practice & take online exams" },
          { icon: "fa-newspaper", text: "Solve previous-year papers" },
          { icon: "fa-clipboard-check", text: "Mock & scheduled tests" },
          { icon: "fa-lightbulb", text: "Instant answer explanations" },
          { icon: "fa-redo", text: "Error exams to fix mistakes" },
        ],
      },
      {
        title: "Know where you stand",
        points: [
          { icon: "fa-chart-line", text: "Track your performance & rank" },
          { icon: "fa-bullseye", text: "AI-driven rank prediction" },
          { icon: "fa-stopwatch", text: "Accuracy & time analysis" },
          { icon: "fa-balance-scale", text: "Strength & weakness reports" },
          { icon: "fa-users", text: "Benchmark against peers" },
        ],
      },
    ],
  },
  parent: {
    headline: "Stay close to",
    accentWord: "every milestone.",
    slides: [
      {
        title: "Always in the loop",
        points: [
          { icon: "fa-user-check", text: "Real-time attendance alerts" },
          { icon: "fa-clipboard-list", text: "Daily activity summary" },
          { icon: "fa-calendar-alt", text: "Class & exam schedule" },
          { icon: "fa-umbrella-beach", text: "Leave & holiday updates" },
          { icon: "fa-bell", text: "Instant push notifications" },
        ],
      },
      {
        title: "Celebrate results",
        points: [
          { icon: "fa-award", text: "Exam results & rank updates" },
          { icon: "fa-chart-line", text: "Progress tracked over time" },
          { icon: "fa-book-open", text: "Subject-wise performance" },
          { icon: "fa-arrow-up", text: "Improvement insights" },
          { icon: "fa-medal", text: "Achievement badges" },
        ],
      },
      {
        title: "Zero hassle",
        points: [
          { icon: "fa-receipt", text: "Fee reminders & receipts" },
          { icon: "fa-credit-card", text: "Online fee payment" },
          { icon: "fa-comments", text: "Direct messages from teachers" },
          { icon: "fa-user-friends", text: "Mentor feedback" },
          { icon: "fa-headset", text: "Help & support on call" },
        ],
      },
    ],
  },
  faculty: {
    headline: "Teach more.",
    accentWord: "Manage less.",
    slides: [
      {
        title: "Run your classroom",
        points: [
          { icon: "fa-user-check", text: "Mark & track attendance" },
          { icon: "fa-calendar-alt", text: "Schedule classes & exams" },
          { icon: "fa-table", text: "Manage your timetable" },
          { icon: "fa-list", text: "Class-wise student lists" },
          { icon: "fa-bolt", text: "Quick roll-call" },
        ],
      },
      {
        title: "Share knowledge",
        points: [
          { icon: "fa-folder-open", text: "Share notes & assignments" },
          { icon: "fa-video", text: "Upload recorded lectures" },
          { icon: "fa-file-signature", text: "Create question papers" },
          { icon: "fa-pen", text: "Set practice exams" },
          { icon: "fa-bullhorn", text: "Push announcements" },
        ],
      },
      {
        title: "Stay informed",
        points: [
          { icon: "fa-chart-line", text: "Monitor student performance" },
          { icon: "fa-exclamation-triangle", text: "Spot at-risk students early" },
          { icon: "fa-layer-group", text: "Class & section analytics" },
          { icon: "fa-chart-bar", text: "Result trends" },
          { icon: "fa-comments", text: "Parent communication" },
        ],
      },
    ],
  },
  management: {
    headline: "Your institute,",
    accentWord: "at a glance.",
    slides: [
      {
        title: "See everything",
        points: [
          { icon: "fa-sitemap", text: "Branch-wide analytics" },
          { icon: "fa-tachometer-alt", text: "Live attendance dashboards" },
          { icon: "fa-user-graduate", text: "Real-time enrolment counts" },
          { icon: "fa-clipboard-list", text: "Daily activity reports" },
          { icon: "fa-building", text: "Multi-campus overview" },
        ],
      },
      {
        title: "Grow the institute",
        points: [
          { icon: "fa-user-plus", text: "Admissions & fee insights" },
          { icon: "fa-coins", text: "Revenue & collections" },
          { icon: "fa-hand-holding-usd", text: "Pending dues tracking" },
          { icon: "fa-funnel-dollar", text: "Lead & enquiry funnel" },
          { icon: "fa-chair", text: "Capacity planning" },
        ],
      },
      {
        title: "Lead with data",
        points: [
          { icon: "fa-users-cog", text: "Staff & faculty oversight" },
          { icon: "fa-trophy", text: "Result & rank trends" },
          { icon: "fa-chart-bar", text: "Performance benchmarks" },
          { icon: "fa-file-export", text: "Exportable reports" },
          { icon: "fa-brain", text: "Data-driven decisions" },
        ],
      },
    ],
  },
};

const GET_ORGANISATION_PROFILE = gql`
  query($institution_id: Int!) {
    getOrganisationProfile(institution_id: $institution_id) {
      id
      complete_address
      logo
    }
  }
`;

const GET_ROLE_MODULE_ACCESS = gql`
  query($institute_id: Int, $employee_role_id: Int) {
    getRoleModuleAccess(institute_id: $institute_id, employee_role_id: $employee_role_id) {
      module_id
      module_name
      module_key
      sub_module_name
      is_view
      is_edit
      is_delete
    }
  }
`;

const STUDENT_LOGIN = gql`
  mutation($username: String!, $password: String) {
    studentLogin(username: $username, password: $password) {
      token
      refreshToken
      user {
        name
        email
        mobile
        valid
        class_id
        exam_id
        branch_id
        exam_name
        current_plan_id
        mobile_verified
        target_year
        videos
        branch_name
        profile_pic
        userlevel
        forum
        institution_id
        institute_name
        institute_logo
      }
    }
  }
`;

const LOGIN_USER = gql`
  mutation($username: String!, $password: String!) {
    login1(username: $username, password: $password) {
      token
      refreshToken
      user {
        username
        userlevel
        email
        mobile
        valid
        name
        institution_id
        institute_name
        module_access
        subject_ids
        class_ids
        section_ids
        branch_id
        employee_role_id
        id
      }
    }
  }
`;

class RoleLogin extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedRole: "student",
      identifier: "",
      password: "",
      submitError: "",
      loader: 0,
      showPassword: false,
    };
  }

  get activeRole() {
    return ROLES.find((r) => r.key === this.state.selectedRole) || ROLES[0];
  }

  selectRole = (key) => {
    // Clear the identifier when switching roles so a mobile number doesn't linger
    // in an email field, etc.
    this.setState({ selectedRole: key, identifier: "", submitError: "" });
  };

  togglePasswordVisibility = () => {
    this.setState((prevState) => ({ showPassword: !prevState.showPassword }));
  };

  handleInputChange = (e) => {
    this.setState({ [e.target.name]: e.target.value });
  };

  handleFormSubmit = (e) => {
    e.preventDefault();
    const { identifier, password, selectedRole } = this.state;
    // Student → studentLogin (student backend). Faculty/Management → login1
    // (institution backend). Parent has no API yet — show an error, call nothing.
    const isStudent = selectedRole === "student";
    const isLogin1 = LOGIN1_ROLES.includes(selectedRole);
    if (!isStudent && !isLogin1) {
      this.setState({ submitError: "Login for this role is not available yet." });
      return;
    }
    if (!identifier || !password) {
      this.setState({ submitError: "Please fill all the values to proceed" });
      return;
    }

    this.setState({ loader: 1, submitError: "" });
    const request = isStudent
      ? this.studentLogin(identifier.trim(), password)
      : this.login1(identifier.trim(), password);
    request.catch((error) => {
      this.setState({
        submitError: error.graphQLErrors
          ? error.graphQLErrors.map((x) => x.message)
          : "Unable to sign in. Please try again.",
        loader: 0,
      });
    });
  };

  // Authenticates against the separate student backend, writes the session
  // cookies on the shared .rizee.in domain, then hands off to the standalone
  // student app's loading page.
  studentLogin = async (username, password) => {
    const result = await studentClient.mutate({
      mutation: STUDENT_LOGIN,
      variables: { username, password },
    });

    const payload = result.data && result.data.studentLogin;
    const u = payload && payload.user;
    if (!u) {
      throw new Error("Unable to sign in. Please try again.");
    }

    // Write cookies on the shared parent domain (.rizee.in) so the standalone
    // student app at student.rizee.in can read them after the redirect below.
    // On localhost (dev) a domained cookie can't be set, so fall back to a
    // host-only cookie there.
    const host = window.location.hostname;
    const opts = host.endsWith("rizee.in") ? { domain: ".rizee.in", path: "/" } : { path: "/" };
    const setCookie = (key, value) => Cookies.set(key, value, opts);

    // Store every detail in cookies before navigating away.
    setCookie("studenttoken", payload.token);
    setCookie("studentrefreshtoken", payload.refreshToken);
    setCookie("studentusername", u.name);
    setCookie("studentemail", u.email);
    setCookie("institute_name", u.institute_name);
    setCookie("institute_logo", u.institute_logo);
    setCookie("mobile", u.mobile);
    setCookie("classid", u.class_id);
    setCookie("branch_id", u.branch_id);
    setCookie("examid", u.exam_id);
    setCookie("exam_name", u.exam_name);
    setCookie("mobileverified", u.mobile_verified);
    setCookie("targetyear", u.target_year);
    setCookie("videos", u.videos);
    setCookie("branch_name", u.branch_name);
    setCookie("role", "student");
    setCookie("profile_pic", u.profile_pic);
    setCookie("student_userlevel", u.userlevel);
    setCookie("stulogintype", "normal");
    setCookie("forumlink", u.forum);
    setCookie("institution_id", u.institution_id);
    setCookie("user_valid", u.valid);
    setCookie("toggle", "wrapper sidebar-enable");

    this.setState({ loader: 0 });
    // Hand off to the standalone student app's loading page. Because the cookies
    // above are on .rizee.in, that app reads the session; it then fetches
    // studentGlobals / isStudentUserValid and forwards on to /student/home.
    window.location.href = "https://student.rizee.in/student/loading";
  };

  login1 = async (username, password) => {
    const result = await this.props.login1({
      variables: { username, password },
      update: (store, { data }) => {
        Cookies.set("token", data.login1.token);
        Cookies.set("refreshtoken", data.login1.refreshToken);
        Cookies.set("username", data.login1.user.username);
        Cookies.set("name", data.login1.user.name);
        Cookies.set("email", data.login1.user.email);
        Cookies.set("id", data.login1.user.id);
        Cookies.set("institutionid", data.login1.user.institution_id);
        Cookies.set("role", "institution");
        Cookies.set("institutename", data.login1.user.institute_name);
        Cookies.set("userlevel", data.login1.user.userlevel);
        Cookies.set("loginRole", this.state.selectedRole);
        localStorage.setItem(
          "moduleRestrictions",
          JSON.stringify(data.login1.user.module_access)
        );
      },
    });

    const userData = result.data.login1.user;
    Cookies.set("branch_id", String(userData.branch_id || ""));
    Cookies.set("class_ids", String(userData.class_ids || ""));
    Cookies.set("section_ids", String(userData.section_ids || ""));
    Cookies.set("subject_ids", String(userData.subject_ids || ""));
    Cookies.set("employee_role_id", String(userData.employee_role_id || ""));
    // Capture the employee's assignment (branch / class / subject ids) so every
    // module can show only the records matching them.
    await resolveUserScope(client, userData);

    try {
      const accessResult = await client.query({
        query: GET_ROLE_MODULE_ACCESS,
        variables: {
          institute_id: parseInt(userData.institution_id),
          employee_role_id: userData.employee_role_id ? parseInt(userData.employee_role_id) : null,
        },
        fetchPolicy: "network-only",
      });
      const roleAccess = (accessResult.data && accessResult.data.getRoleModuleAccess) || [];
      localStorage.setItem("roleModuleAccess", JSON.stringify(roleAccess));
    } catch (accessError) {
      console.error("Error fetching role module access:", accessError);
      localStorage.setItem("roleModuleAccess", JSON.stringify([]));
    }

    try {
      const orgResult = await client.query({
        query: GET_ORGANISATION_PROFILE,
        variables: { institution_id: parseInt(userData.institution_id) },
        fetchPolicy: "network-only",
      });
      const org = orgResult.data && orgResult.data.getOrganisationProfile;
      if (org) {
        Cookies.set("address", org.complete_address || "");
        Cookies.set(
          "url",
          org.logo ? `https://support.rizee.in/files/${org.logo}` : ""
        );
      }
    } catch (orgError) {
      console.error("Error fetching organisation profile:", orgError);
    }

    this.setState({ loader: 0 });
    if (result.data.login1) {
      // ── OLD CODE (kept for reference / rollback) ──────────────────────────
      // Everyone landed on the Dashboard, granted or not.
      //
      // this.props.history.push("/dashboard");

      // ── NEW CODE ─────────────────────────────────────────────────────────
      // First module this role actually has View access to. See Login.jsx for the note.
      this.props.history.push(getFirstAccessibleRoute());
    }
  };

  // Vertically auto-scrolling campus ticker. The list is duplicated so the
  // translateY loop is seamless; speed scales with how many campuses there are.
  renderCampuses() {
    if (CAMPUSES.length <= 1) {
      return <span>{CAMPUSES[0] || "Nizampet Campus"}</span>;
    }
    return (
      <span className="role_login_campus">
        <span
          className="role_login_campus_track"
          style={{ animationDuration: `${CAMPUSES.length * 2}s` }}
        >
          {CAMPUSES.concat(CAMPUSES).map((c, i) => (
            <span className="role_login_campus_item" key={i}>
              {c}
            </span>
          ))}
        </span>
      </span>
    );
  }

  render() {
    // A valid token is the only requirement to be considered signed in — this
    // matches the app-wide AuthGate (App.js) and HomeDashboard, which both gate
    // on `token` alone. Faculty (and other branch-level roles) can come back
    // with a null institution_id, so requiring `institutionid` here wrongly kept
    // them stuck on the login page despite a valid session.
    // ── OLD CODE (kept for reference / rollback) ─────────────────────────────
    // this.props.history.push("/dashboard");
    //
    // ── NEW CODE ─────────────────────────────────────────────────────────────
    // Already-signed-in users bounce to their first View-accessible module, not to a
    // Dashboard their role may not be granted.
    if (Cookies.get("token") !== undefined) {
      this.props.history.push(getFirstAccessibleRoute());
    }

    const role = this.activeRole;
    const intro = ROLE_INTRO[role.key] || ROLE_INTRO.student;

    return (
      <div className="role_login">
        {/* LEFT — branding / marketing panel; copy + tint follow the active role */}
        <div
          className="role_login_intro"
          style={{ "--rl-role": role.accent, "--rl-role2": role.accent2 }}
        >
          {/* per-role colour wash over the nav-bg artwork */}
          <span className="role_login_bg_tint" />

          <div className="role_login_brand">
            <span className="role_login_logo_badge">
              <img src={vignanLogo} alt="Vignan Junior College" />
            </span>
            <div>
              <h2>Vignan Junior College</h2>
              {this.renderCampuses()}
            </div>
          </div>

          {/* keyed so it re-plays the entrance animation + restarts the
              carousel from slide 1 whenever the selected role changes */}
          <div className="role_login_copy" key={role.key}>
            <span className="role_login_chip">
              <i className={`fas ${role.icon}`} /> {role.title} portal
            </span>
            <h1 className="role_login_headline">
              {intro.headline} <span className="accent">{intro.accentWord}</span>
            </h1>

            <Carousel
              className="role_login_carousel"
              controls={false}
              indicators
              fade
              interval={2800}
              pause={false}
            >
              {intro.slides.map((slide, si) => (
                <Carousel.Item key={si}>
                  <h5 className="role_login_slide_title">{slide.title}</h5>
                  <ul className="role_login_features">
                    {slide.points.map((f, i) => (
                      <li key={i}>
                        <span className="role_login_feature_icon">
                          <i className={`fas ${f.icon}`} />
                        </span>
                        {f.text}
                      </li>
                    ))}
                  </ul>
                </Carousel.Item>
              ))}
            </Carousel>
          </div>

          {/* <p className="role_login_powered">Powered by Rizee · MOU 2025</p> */}
        </div>

        {/* RIGHT — role selection + login card */}
        <div className="role_login_panel">
          <div className="role_login_card">
            {/* Mobile-only brand header — the left intro panel is hidden on
                small screens, so surface the Vignan identity here too */}
            <div className="role_login_mobile_brand">
              <span className="role_login_mobile_logo">
                <img src={vignanLogo} alt="Vignan Junior College" />
              </span>
              <div>
                <h2>Vignan Junior College</h2>
                {this.renderCampuses()}
              </div>
            </div>

            <h1 className="role_login_title">Welcome back</h1>
            <p className="role_login_caption">Select your role and sign in to continue</p>

            <span className="role_login_iam">I am a…</span>
            <div className="role_login_grid">
              {ROLES.map((r) => (
                <button
                  type="button"
                  key={r.key}
                  className={`role_login_role ${this.state.selectedRole === r.key ? "is-active" : ""}`}
                  onClick={() => this.selectRole(r.key)}
                  style={{ "--rl-accent": r.accent, "--rl-accent2": r.accent2 }}
                >
                  <span
                    className="role_login_role_icon"
                    style={{
                      background: `linear-gradient(135deg, ${r.accent}, ${r.accent2})`,
                    }}
                  >
                    <i className={`fas ${r.icon}`} />
                  </span>
                  <span className="role_login_role_text">
                    <span className="role_login_role_title">{r.title}</span>
                    <span className="role_login_role_sub">{r.sub}</span>
                  </span>
                  <i className="fas fa-check role_login_role_check" />
                </button>
              ))}
            </div>

            <Form onSubmit={this.handleFormSubmit}>
              {this.state.submitError ? (
                <span className="role_login_error">{this.state.submitError}</span>
              ) : null}

              <Form.Group className="role_login_group">
                <Form.Label className="role_login_label">{role.label}</Form.Label>
                <div className="role_login_input_wrap">
                  <i className={`fas ${role.inputIcon} role_login_input_icon`} />
                  <Form.Control
                    type="text"
                    name="identifier"
                    className="role_login_input"
                    placeholder={role.placeholder}
                    value={this.state.identifier}
                    onChange={this.handleInputChange}
                    autoComplete="off"
                  />
                </div>
              </Form.Group>

              <Form.Group className="role_login_group">
                <Form.Label className="role_login_label">Password</Form.Label>
                <div className="role_login_input_wrap">
                  <i className="fas fa-lock role_login_input_icon" />
                  <Form.Control
                    type={this.state.showPassword ? "text" : "password"}
                    name="password"
                    className="role_login_input"
                    placeholder={role.key === "student" ? "e.g. 01/01/2000" : "••••••••"}
                    value={this.state.password}
                    onChange={this.handleInputChange}
                  />
                  <i
                    className={`fas ${this.state.showPassword ? "fa-eye-slash" : "fa-eye"} role_login_eye`}
                    onClick={this.togglePasswordVisibility}
                  />
                </div>
              </Form.Group>

              {this.state.loader === 1 ? (
                <Button className="role_login_submit" type="submit" disabled>
                  <span className="spinner-border spinner-border-sm mr-2" />
                  Signing in…
                </Button>
              ) : (
                <Button className="role_login_submit" type="submit">
                  Sign In <i className="fas fa-arrow-right ml-2" />
                </Button>
              )}
            </Form>

            {/* <div className="role_login_demo">
              <i className="fas fa-lock-open mr-2" />
              <span>
                <b>Demo mode</b> — pick any role and click “Sign In”. No real
                credentials needed.
              </span>
            </div> */}

            {/* <p className="role_login_foot">
              New to Vignan?{" "}
              <a href="https://vignanjuniorcolleges.org/" className="role_login_link">
                Create an account
              </a>{" "}
              ·{" "}
              <a href="https://vignanjuniorcolleges.org/" className="role_login_link">
                ← Back to site
              </a>
            </p> */}

            <p className="role_login_powered_card">
              Powered by <b>Rizee</b> <i className="fas fa-bolt" /> · A product of
              myLearningPlus pvt ltd
            </p>
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(
  graphql(LOGIN_USER, {
    name: "login1",
  })(RoleLogin)
);
